import { useState, useRef, useEffect } from "react";
import { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { AiMarkdown } from "../components/AiMarkdown";
import { motion } from "framer-motion";
import { MessageSquareCode, Send, Bot, User, Loader2, Sparkles } from "lucide-react";

const QUICK = [
  "Fix BSOD 0x0000007B on my Windows 11 laptop",
  "Outlook stuck on 'Loading Profile'",
  "Windows activation error 0xC004C003",
  "Teams is not detecting my camera",
];

const sessionId = `sess-${Math.random().toString(36).slice(2)}-${Date.now()}`;

const Assistant = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: content }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith("data:")) continue;
          const payload = JSON.parse(trimmed.slice(5).trim());
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                role: "assistant",
                content: copy[copy.length - 1].content + payload.delta,
              };
              return copy;
            });
          }
          if (payload.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: `⚠️ ${payload.error}` };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "⚠️ Connection error. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-primary/12 border border-primary/25 grid place-items-center">
          <MessageSquareCode className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">AI Assistant</h1>
          <p className="text-sm text-muted-foreground">Describe any Microsoft problem — get a diagnosis and fix instantly.</p>
        </div>
      </div>

      <div className="glass rounded-3xl flex flex-col h-[62vh]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-5" data-testid="chat-thread">
          {messages.length === 0 && (
            <div className="h-full grid place-items-center text-center px-6">
              <div>
                <div className="mx-auto h-16 w-16 rounded-2xl bg-primary/10 border border-primary/25 grid place-items-center glow-blue">
                  <Sparkles className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-semibold mt-4">How can I help fix your laptop?</h3>
                <p className="text-sm text-muted-foreground mt-1.5">Try one of these common issues:</p>
                <div className="flex flex-wrap justify-center gap-2 mt-5">
                  {QUICK.map((q, i) => (
                    <button key={i} onClick={() => send(q)}
                      className="text-sm px-3.5 py-2 rounded-full border border-border/70 bg-secondary/40 hover:border-primary/50 hover:text-primary transition-all"
                      data-testid={`quick-prompt-${i}`}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`shrink-0 h-9 w-9 rounded-lg grid place-items-center ${
                msg.role === "user" ? "bg-secondary" : "bg-primary/15 border border-primary/25"}`}>
                {msg.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4 text-primary" />}
              </div>
              <div className={`max-w-[82%] rounded-2xl px-4 py-3 ${
                msg.role === "user"
                  ? "bg-primary text-primary-foreground rounded-tr-sm"
                  : "bg-secondary/50 border border-border/60 rounded-tl-sm"}`}>
                {msg.role === "user" ? (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                ) : msg.content === "" && streaming ? (
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Diagnosing…
                  </p>
                ) : (
                  <div className={streaming && i === messages.length - 1 ? "blink-cursor" : ""}>
                    <AiMarkdown text={msg.content} testid={`ai-response-${i}`} />
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="border-t border-border/60 p-4">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Describe the issue, e.g. 'Excel keeps crashing on startup'…"
              className="bg-secondary/40 border-border/70 h-12 rounded-xl"
              data-testid="ai-chat-input"
              disabled={streaming}
            />
            <Button type="submit" disabled={streaming || !input.trim()}
              className="h-12 w-12 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground shrink-0"
              data-testid="ai-chat-send-btn">
              {streaming ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Assistant;
